import {
    Alert,
    Button,
    Card,
    Checkbox,
    DatePicker,
    Divider,
    Empty,
    Form,
    Input,
    Modal,
    notification,
    Radio,
    Result,
    Select,
    Skeleton,
    Space,
    Tag,
    Typography,
} from "antd";
import { Copy, KeyRound, Trash2, Plus, FolderTree, Box, AlertCircle, CheckCircle2 } from "lucide-react";
import { Dayjs } from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { ApiKeyMeta, ApiKeyScope, Permission } from "@microtica/ms-project-sdk";

import { getEnvironmentService, getProjectService, isApiKeysEnabled } from "../../backend";
import { useDoubleConfigurationModal } from "../../contexts/Modal";

/**
 * Entity types we expose in the scope picker. Matches the lowercase
 * `EntityPermissionType` strings the backend accepts on
 * POST /keys/{keyId}/scopes — the SDK only declares `entityType` as a
 * raw string, so we narrow it here for the UI.
 */
type EntityKind =
    | "project"
    | "stage"
    | "pipeline"
    | "resource"
    | "kubernetes"
    | "component"
    | "awsaccount"
    | "microservice"
    | "gitaccount"
    | "azureaccount"
    | "gcpaccount";

const PERMISSION_OPTIONS: Permission[] = [
    Permission.Read,
    Permission.Write,
    Permission.Admin,
    Permission.Owner,
];

/**
 * Permission strength ordering. Used to enforce the "floor + upgrades"
 * model: a per-env scope is only meaningful when *strictly stronger*
 * than the project-level baseline. Anything ≤ baseline is a no-op once
 * the backend's OR-resolution kicks in, so the UI prevents it.
 */
const PERM_ORDER: Record<Permission, number> = {
    [Permission.Read]: 0,
    [Permission.Write]: 1,
    [Permission.Admin]: 2,
    [Permission.Owner]: 3,
};
type Baseline = "none" | Permission;

function permissionsStrongerThan(b: Baseline): Permission[] {
    if (b === "none") return PERMISSION_OPTIONS;
    return PERMISSION_OPTIONS.filter(p => PERM_ORDER[p] > PERM_ORDER[b]);
}

interface EnvOption {
    id: string;
    name: string;
}

/** Internal shape used by the scope picker for both modals. */
interface ScopeSelection {
    project: { selected: boolean; permission: Permission };
    envs: Record<string, { selected: boolean; permission: Permission }>;
}

/**
 * Build a fresh ScopeSelection with a chosen baseline level.
 *   - `baseline === "none"` → no project scope.
 *   - otherwise the project scope is selected at that permission.
 * Per-env upgrades start unchecked at the lowest level strictly above
 * the baseline (so picking one is a no-op if the user doesn't touch
 * the permission).
 */
const freshScopeSelection = (
    envs: EnvOption[],
    baseline: Baseline,
): ScopeSelection => {
    const defaultEnvPerm = permissionsStrongerThan(baseline)[0] ?? Permission.Write;
    return {
        project:
            baseline === "none"
                ? { selected: false, permission: Permission.Write }
                : { selected: true, permission: baseline },
        envs: Object.fromEntries(
            envs.map(e => [e.id, { selected: false, permission: defaultEnvPerm }]),
        ),
    };
};

function scopeCount(s: ScopeSelection): number {
    return (s.project.selected ? 1 : 0) + Object.values(s.envs).filter(e => e.selected).length;
}

type ExpiryPreset = "never" | "7d" | "30d" | "90d" | "1y" | "custom";

const EXPIRY_PRESET_LABELS: Record<ExpiryPreset, string> = {
    never: "Never",
    "7d": "7 days",
    "30d": "30 days",
    "90d": "90 days",
    "1y": "1 year",
    custom: "Custom",
};

function expiryToEpoch(preset: ExpiryPreset, customDate?: Dayjs): number | undefined {
    if (preset === "never") return undefined;
    if (preset === "custom") return customDate?.valueOf();
    const now = Date.now();
    switch (preset) {
        case "7d": return now + 7 * 86400 * 1000;
        case "30d": return now + 30 * 86400 * 1000;
        case "90d": return now + 90 * 86400 * 1000;
        case "1y": return now + 365 * 86400 * 1000;
    }
}

const ProjectSettingsApiKeys = () => {
    const { projectId } = useParams() as { projectId: string };
    const featureEnabled = isApiKeysEnabled(projectId);
    const [loading, setLoading] = useState(true);
    const [keys, setKeys] = useState<ApiKeyMeta[]>([]);
    const [scopesByKey, setScopesByKey] = useState<Record<string, ApiKeyScope[]>>({});
    const [envOptions, setEnvOptions] = useState<EnvOption[]>([]);

    // Create wizard state.
    const [createOpen, setCreateOpen] = useState(false);
    const [createStep, setCreateStep] = useState<"form" | "plaintext">("form");
    const [createSubmitting, setCreateSubmitting] = useState(false);
    const [mintedPlaintext, setMintedPlaintext] = useState<string | null>(null);
    const [mintedScopeSummary, setMintedScopeSummary] = useState<string[]>([]);

    // Add-scope modal state.
    const [addScopeKey, setAddScopeKey] = useState<ApiKeyMeta | null>(null);

    // Revoked keys stay in the DB (audit trail) but clutter the active view —
    // hide them by default, surface a toggle when any exist.
    const [showRevoked, setShowRevoked] = useState(false);

    const { open: openConfirmModal } = useDoubleConfigurationModal();

    useEffect(() => {
        if (!featureEnabled) return;
        void loadAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId, featureEnabled]);

    async function loadAll(opts: { silent?: boolean } = {}) {
        // Only show the page-level Skeleton on the very first load. Silent
        // refreshes (after add-scope / remove-scope / revoke / create) leave
        // the existing cards in place so the user keeps their context.
        if (!opts.silent) {
            setLoading(true);
        }
        try {
            const [keysResp, stagesResp] = await Promise.all([
                getProjectService().listApiKeys(projectId),
                getEnvironmentService().getStages(projectId),
            ]);
            const fetchedKeys = keysResp.data.keys;
            setKeys(fetchedKeys);
            const stages = (stagesResp.data.stages ?? []).map(s => ({
                id: s.id!,
                name: s.name ?? s.id!,
            }));
            setEnvOptions(stages);
            const scopeMap: Record<string, ApiKeyScope[]> = {};
            await Promise.all(
                fetchedKeys.map(async k => {
                    try {
                        const { data } = await getProjectService().listApiKeyScopes(projectId, k.keyId);
                        scopeMap[k.keyId] = data.scopes;
                    } catch {
                        scopeMap[k.keyId] = [];
                    }
                }),
            );
            setScopesByKey(scopeMap);
        } catch (err: any) {
            notification.error({
                message: "Failed to load API keys",
                description: err?.response?.data?.message ?? err?.message,
            });
        } finally {
            if (!opts.silent) {
                setLoading(false);
            }
        }
    }

    function handleRevokeKey(key: ApiKeyMeta) {
        openConfirmModal({
            title: "Revoke API key?",
            description: `Subsequent requests using "${key.name}" will be rejected. This cannot be undone.`,
            confirmation: key.name,
            okText: "Revoke key",
            cancelText: "Cancel",
            onOk: async () => {
                try {
                    await getProjectService().revokeApiKey(projectId, key.keyId);
                    notification.success({ message: `Revoked "${key.name}"` });
                    await loadAll({ silent: true });
                } catch (err: any) {
                    notification.error({
                        message: "Revoke failed",
                        description: err?.response?.data?.message ?? err?.message,
                    });
                }
            },
        });
    }

    async function handleRemoveScope(keyId: string, entityId: string) {
        // Optimistic update — drop the row from local state immediately so
        // the tag disappears without waiting on a network round-trip. The
        // silent refresh below reconciles with the server in case anything
        // else changed between renders.
        setScopesByKey(prev => ({
            ...prev,
            [keyId]: (prev[keyId] ?? []).filter(s => s.entityId !== entityId),
        }));
        try {
            await getProjectService().revokeApiKeyScope(projectId, keyId, entityId);
            notification.success({ message: "Scope removed" });
            await loadAll({ silent: true });
        } catch (err: any) {
            notification.error({
                message: "Failed to remove scope",
                description: err?.response?.data?.message ?? err?.message,
            });
            // Roll the optimistic update back by re-fetching authoritative state.
            await loadAll({ silent: true });
        }
    }

    /** Apply a ScopeSelection to a (newly-created or existing) key. */
    async function applyScopeSelection(keyId: string, sel: ScopeSelection): Promise<string[]> {
        const tasks: { entityId: string; entityType: EntityKind; permission: Permission; label: string }[] = [];
        if (sel.project.selected) {
            tasks.push({
                entityId: projectId,
                entityType: "project",
                permission: sel.project.permission,
                label: `Project · ${sel.project.permission}`,
            });
        }
        for (const env of envOptions) {
            const e = sel.envs[env.id];
            if (e?.selected) {
                tasks.push({
                    entityId: env.id,
                    entityType: "stage",
                    permission: e.permission,
                    label: `env: ${env.name} · ${e.permission}`,
                });
            }
        }
        const summary: string[] = [];
        for (const t of tasks) {
            try {
                await getProjectService().grantApiKeyScope(projectId, keyId, {
                    entityId: t.entityId,
                    entityType: t.entityType,
                    permission: t.permission,
                });
                summary.push(t.label);
            } catch (err: any) {
                notification.warning({
                    message: `Couldn't grant ${t.label}`,
                    description: err?.response?.data?.message ?? err?.message,
                });
            }
        }
        return summary;
    }

    async function handleCreateSubmit(values: {
        name: string;
        expiryPreset: ExpiryPreset;
        expiryCustom?: Dayjs;
        scopes: ScopeSelection;
    }) {
        setCreateSubmitting(true);
        try {
            const expiresAt = expiryToEpoch(values.expiryPreset, values.expiryCustom);
            const { data: minted } = await getProjectService().createApiKey(
                projectId,
                { name: values.name, expiresAt },
            );
            const summary = await applyScopeSelection(minted.keyId, values.scopes);
            setMintedPlaintext(minted.plaintext);
            setMintedScopeSummary(summary);
            setCreateStep("plaintext");
            void loadAll({ silent: true });
        } catch (err: any) {
            notification.error({
                message: "Failed to create key",
                description: err?.response?.data?.message ?? err?.message,
            });
        } finally {
            setCreateSubmitting(false);
        }
    }

    async function handleAddScopeSubmit(sel: ScopeSelection) {
        if (!addScopeKey) return;
        await applyScopeSelection(addScopeKey.keyId, sel);
        notification.success({ message: "Scopes added" });
        setAddScopeKey(null);
        await loadAll({ silent: true });
    }

    function openCreate() {
        setCreateStep("form");
        setMintedPlaintext(null);
        setMintedScopeSummary([]);
        setCreateOpen(true);
    }

    function closeCreate() {
        setCreateOpen(false);
        // Reset shortly after the modal animates out so we don't flash content.
        setTimeout(() => {
            setCreateStep("form");
            setMintedPlaintext(null);
            setMintedScopeSummary([]);
        }, 200);
    }

    const sortedKeys = useMemo(
        () => [...keys].sort((a, b) => b.createdAt - a.createdAt),
        [keys],
    );
    const revokedCount = useMemo(() => keys.filter(k => !!k.revokedAt).length, [keys]);
    const visibleKeys = useMemo(
        () => showRevoked ? sortedKeys : sortedKeys.filter(k => !k.revokedAt),
        [sortedKeys, showRevoked],
    );

    if (!featureEnabled) {
        return (
            <Card bordered>
                <Result
                    status="info"
                    title="API keys are not enabled for this project"
                    subTitle="Reach out to your Microtica contact to enable the feature."
                />
            </Card>
        );
    }

    if (loading) {
        return <Skeleton active />;
    }

    return (
        <>
            <Card bordered style={{ marginBottom: 16 }}>
                <Card.Meta
                    title="API keys"
                    description="Long-lived credentials for the Microtica CLI, SDK, and external automation. Keys belong to this project — they're visible only here, survive when team members leave, and act on behalf of the project (not the person who minted them). Scope each key to specific environments and revoke at any time."
                />
                <Divider />
                <Button
                    type="primary"
                    icon={<Plus size={14} />}
                    onClick={openCreate}
                >
                    Create API key
                </Button>
            </Card>

            {revokedCount > 0 && (
                <div style={{ marginBottom: 12, textAlign: "right" }}>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => setShowRevoked(s => !s)}
                    >
                        {showRevoked
                            ? `Hide revoked (${revokedCount})`
                            : `Show revoked (${revokedCount})`}
                    </Button>
                </div>
            )}

            {sortedKeys.length === 0 ? (
                <Card>
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description="No API keys yet. Create your first one to get started."
                    />
                </Card>
            ) : visibleKeys.length === 0 ? (
                <Card>
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={`No active keys. ${revokedCount} revoked — click "Show revoked" to view them.`}
                    />
                </Card>
            ) : (
                <Space direction="vertical" size={12} style={{ width: "100%" }}>
                    {visibleKeys.map(key => (
                        <KeyCard
                            key={key.keyId}
                            apiKey={key}
                            scopes={scopesByKey[key.keyId] ?? []}
                            projectId={projectId}
                            envOptions={envOptions}
                            onAddScope={() => setAddScopeKey(key)}
                            onRevoke={() => handleRevokeKey(key)}
                            onRemoveScope={entityId => handleRemoveScope(key.keyId, entityId)}
                        />
                    ))}
                </Space>
            )}

            <CreateKeyModal
                open={createOpen}
                step={createStep}
                submitting={createSubmitting}
                envOptions={envOptions}
                projectId={projectId}
                plaintext={mintedPlaintext}
                scopeSummary={mintedScopeSummary}
                onSubmit={handleCreateSubmit}
                onClose={closeCreate}
            />

            <AddScopeModal
                open={!!addScopeKey}
                keyName={addScopeKey?.name}
                envOptions={envOptions}
                projectId={projectId}
                existingScopes={addScopeKey ? scopesByKey[addScopeKey.keyId] ?? [] : []}
                onSubmit={handleAddScopeSubmit}
                onClose={() => setAddScopeKey(null)}
            />
        </>
    );
};

// ────────────────────────────────────────────────────────────────────────────────
// Subcomponents
// ────────────────────────────────────────────────────────────────────────────────

interface KeyCardProps {
    apiKey: ApiKeyMeta;
    scopes: ApiKeyScope[];
    projectId: string;
    envOptions: EnvOption[];
    onAddScope: () => void;
    onRevoke: () => void;
    onRemoveScope: (entityId: string) => void;
}

function KeyCard(props: KeyCardProps) {
    const { apiKey, scopes, projectId, envOptions, onAddScope, onRevoke, onRemoveScope } = props;
    const revoked = !!apiKey.revokedAt;
    const expired = !revoked && !!apiKey.expiresAt && apiKey.expiresAt < Date.now();
    const status: { color: string; label: string } = revoked
        ? { color: "red", label: "Revoked" }
        : expired
            ? { color: "orange", label: "Expired" }
            : { color: "green", label: "Active" };

    const projectScopes = scopes.filter(s => s.entityType === "project");
    const envScopes = scopes.filter(s => s.entityType === "stage");
    const otherScopes = scopes.filter(s => s.entityType !== "project" && s.entityType !== "stage");
    // Add scope is meaningful unless the key is locked at the strongest
    // possible state. With floor+upgrades you can always add either a
    // baseline (if none exists yet) or an env upgrade stronger than the
    // existing baseline — except when the existing baseline is already
    // `owner` (nothing stronger to upgrade to).
    const existingBaselineIsOwner = projectScopes.some(s => s.permission === Permission.Owner);
    const canAddMoreScopes = !revoked && !existingBaselineIsOwner;

    return (
        <Card>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
                <Space direction="vertical" size={2}>
                    <Space size={8} align="center">
                        <KeyRound size={16} />
                        <Typography.Text strong style={{ fontSize: 16 }}>
                            {apiKey.name}
                        </Typography.Text>
                        <Tag color={status.color} style={{ margin: 0 }}>{status.label}</Tag>
                    </Space>
                    <Typography.Text type="secondary" style={{ fontFamily: "monospace", fontSize: 12 }}>
                        mic_{apiKey.keyPrefix}_…
                    </Typography.Text>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        Created {new Date(apiKey.createdAt).toLocaleDateString()}
                        {apiKey.expiresAt && ` · Expires ${new Date(apiKey.expiresAt).toLocaleDateString()}`}
                        {" · "}
                        {apiKey.lastUsedAt
                            ? `Last used ${new Date(apiKey.lastUsedAt).toLocaleString()}`
                            : "Never used"}
                    </Typography.Text>
                </Space>
                {!revoked && (
                    <Button danger icon={<Trash2 size={14} />} onClick={onRevoke}>
                        Revoke
                    </Button>
                )}
            </div>

            <Divider style={{ margin: "12px 0" }} />

            {scopes.length === 0 ? (
                <Space>
                    <AlertCircle size={14} color="#d48806" />
                    <Typography.Text type="warning">
                        No scopes — key cannot access anything until you add one.
                    </Typography.Text>
                    {canAddMoreScopes && (
                        <Button size="small" icon={<Plus size={12} />} onClick={onAddScope}>
                            Add scope
                        </Button>
                    )}
                </Space>
            ) : (
                <Space direction="vertical" size={8} style={{ width: "100%" }}>
                    {projectScopes.length > 0 && (
                        <ScopeRow
                            icon={<FolderTree size={14} />}
                            label="Project"
                            tags={projectScopes.map(s => ({
                                entityId: s.entityId,
                                display: s.entityId === projectId
                                    ? `This project · ${s.permission}`
                                    : `${s.entityId} · ${s.permission}`,
                                permission: s.permission,
                            }))}
                            revoked={revoked}
                            onRemove={onRemoveScope}
                        />
                    )}
                    {envScopes.length > 0 && (
                        <ScopeRow
                            icon={<Box size={14} />}
                            label="Environments"
                            tags={envScopes.map(s => {
                                const env = envOptions.find(e => e.id === s.entityId);
                                return {
                                    entityId: s.entityId,
                                    display: `${env?.name ?? s.entityId} · ${s.permission}`,
                                    permission: s.permission,
                                };
                            })}
                            revoked={revoked}
                            onRemove={onRemoveScope}
                        />
                    )}
                    {otherScopes.length > 0 && (
                        <ScopeRow
                            icon={<KeyRound size={14} />}
                            label="Other"
                            tags={otherScopes.map(s => ({
                                entityId: s.entityId,
                                display: `${s.entityType}: ${s.entityId} · ${s.permission}`,
                                permission: s.permission,
                            }))}
                            revoked={revoked}
                            onRemove={onRemoveScope}
                        />
                    )}
                    {canAddMoreScopes && (
                        <Button size="small" type="link" icon={<Plus size={12} />} onClick={onAddScope}>
                            Add scope
                        </Button>
                    )}
                </Space>
            )}
        </Card>
    );
}

function ScopeRow(props: {
    icon: React.ReactNode;
    label: string;
    tags: { entityId: string; display: string; permission: Permission }[];
    revoked: boolean;
    onRemove: (entityId: string) => void;
}) {
    const colorFor = (p: Permission): string => {
        switch (p) {
            case Permission.Owner: return "purple";
            case Permission.Admin: return "magenta";
            case Permission.Write: return "blue";
            case Permission.Read: return "cyan";
        }
    };
    return (
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 140, color: "#666" }}>
                {props.icon}
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>{props.label}</Typography.Text>
            </div>
            <Space wrap size={[6, 6]} style={{ flex: 1 }}>
                {props.tags.map(t => (
                    <Tag
                        key={t.entityId}
                        color={colorFor(t.permission)}
                        closable={!props.revoked}
                        onClose={e => {
                            e.preventDefault();
                            props.onRemove(t.entityId);
                        }}
                    >
                        {t.display}
                    </Tag>
                ))}
            </Space>
        </div>
    );
}

// ────────────────────────────────────────────────────────────────────────────────
// Create wizard modal
// ────────────────────────────────────────────────────────────────────────────────

interface CreateKeyModalProps {
    open: boolean;
    step: "form" | "plaintext";
    submitting: boolean;
    envOptions: EnvOption[];
    projectId: string;
    plaintext: string | null;
    scopeSummary: string[];
    onSubmit: (values: {
        name: string;
        expiryPreset: ExpiryPreset;
        expiryCustom?: Dayjs;
        scopes: ScopeSelection;
    }) => Promise<void>;
    onClose: () => void;
}

function CreateKeyModal(props: CreateKeyModalProps) {
    const [name, setName] = useState("");
    const [expiryPreset, setExpiryPreset] = useState<ExpiryPreset>("30d");
    const [expiryCustom, setExpiryCustom] = useState<Dayjs | undefined>(undefined);
    // Default baseline is `read`: gives CLI/SDK discovery (list envs,
    // pipelines, apps) out of the box. Operator can drop it to None for
    // strict per-env keys with no discovery.
    const [scopes, setScopes] = useState<ScopeSelection>(
        freshScopeSelection(props.envOptions, Permission.Read),
    );

    useEffect(() => {
        if (props.open && props.step === "form") {
            setName("");
            setExpiryPreset("30d");
            setExpiryCustom(undefined);
            setScopes(freshScopeSelection(props.envOptions, Permission.Read));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.open, props.step]);

    const canSubmit = name.trim().length > 0
        && scopeCount(scopes) > 0
        && (expiryPreset !== "custom" || !!expiryCustom);

    const isPlaintextStep = props.step === "plaintext";

    return (
        <Modal
            title={isPlaintextStep ? "API key created" : "Create API key"}
            open={props.open}
            onCancel={isPlaintextStep ? undefined : props.onClose}
            footer={
                isPlaintextStep ? (
                    <Button type="primary" onClick={props.onClose}>
                        I've saved it
                    </Button>
                ) : (
                    <Space>
                        <Button onClick={props.onClose}>Cancel</Button>
                        <Button
                            type="primary"
                            loading={props.submitting}
                            disabled={!canSubmit}
                            onClick={() =>
                                props.onSubmit({ name, expiryPreset, expiryCustom, scopes })
                            }
                        >
                            Create key
                        </Button>
                    </Space>
                )
            }
            closable={!isPlaintextStep}
            maskClosable={!isPlaintextStep}
            width={640}
            destroyOnClose
        >
            {isPlaintextStep ? (
                <PlaintextStep
                    plaintext={props.plaintext ?? ""}
                    scopeSummary={props.scopeSummary}
                />
            ) : (
                <Form layout="vertical">
                    <Form.Item
                        label="Name"
                        required
                        extra="Human-readable label. Shown in the list and used in audit logs."
                    >
                        <Input
                            placeholder="e.g. claude code, ci pipeline"
                            value={name}
                            onChange={e => setName(e.target.value)}
                            autoFocus
                        />
                    </Form.Item>
                    <Form.Item label="Expires" extra="When the key automatically stops working.">
                        <Radio.Group
                            value={expiryPreset}
                            onChange={e => setExpiryPreset(e.target.value)}
                            optionType="button"
                            buttonStyle="solid"
                        >
                            {(Object.keys(EXPIRY_PRESET_LABELS) as ExpiryPreset[]).map(p => (
                                <Radio.Button key={p} value={p}>
                                    {EXPIRY_PRESET_LABELS[p]}
                                </Radio.Button>
                            ))}
                        </Radio.Group>
                        {expiryPreset === "custom" && (
                            <div style={{ marginTop: 8 }}>
                                <DatePicker
                                    value={expiryCustom}
                                    onChange={d => setExpiryCustom(d ?? undefined)}
                                    disabledDate={d => d.valueOf() < Date.now()}
                                    style={{ width: "100%" }}
                                />
                            </div>
                        )}
                    </Form.Item>
                    <Divider style={{ margin: "16px 0" }}>Permission scopes</Divider>
                    {scopeCount(scopes) === 0 && (
                        <Alert
                            type="warning"
                            showIcon
                            message="Pick at least one scope — without one the key can't access anything."
                            style={{ marginBottom: 12 }}
                        />
                    )}
                    <ScopePicker
                        projectId={props.projectId}
                        envOptions={props.envOptions}
                        value={scopes}
                        onChange={setScopes}
                    />
                </Form>
            )}
        </Modal>
    );
}

function PlaintextStep(props: { plaintext: string; scopeSummary: string[] }) {
    return (
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
            <Alert
                type="warning"
                showIcon
                message="This is the only time the plaintext is displayed."
                description="Copy it into your CLI / secret store now. Microtica does not store it; we cannot show it again."
            />
            <Input.TextArea
                value={props.plaintext}
                readOnly
                autoSize
                style={{ fontFamily: "monospace", fontSize: 13 }}
            />
            <Button
                icon={<Copy size={14} />}
                onClick={() => {
                    void navigator.clipboard.writeText(props.plaintext);
                    notification.success({ message: "Copied to clipboard" });
                }}
            >
                Copy to clipboard
            </Button>
            {props.scopeSummary.length > 0 && (
                <>
                    <Divider style={{ margin: "4px 0" }}>Scopes granted</Divider>
                    <Space direction="vertical" size={4}>
                        {props.scopeSummary.map(s => (
                            <Space key={s} size={6}>
                                <CheckCircle2 size={14} color="#52c41a" />
                                <Typography.Text>{s}</Typography.Text>
                            </Space>
                        ))}
                    </Space>
                </>
            )}
            <Typography.Paragraph type="secondary" style={{ margin: 0, fontSize: 12 }}>
                Use it with the CLI: <Typography.Text code>microtica login --token &lt;paste&gt;</Typography.Text>
            </Typography.Paragraph>
        </Space>
    );
}

// ────────────────────────────────────────────────────────────────────────────────
// Add-scope modal (post-creation, for an existing key)
// ────────────────────────────────────────────────────────────────────────────────

interface AddScopeModalProps {
    open: boolean;
    keyName?: string;
    envOptions: EnvOption[];
    projectId: string;
    existingScopes: ApiKeyScope[];
    onSubmit: (sel: ScopeSelection) => Promise<void>;
    onClose: () => void;
}

function AddScopeModal(props: AddScopeModalProps) {
    // Add-scope starts with nothing selected — the user is *adding* to
    // existing state. The picker reads `existingScopes` for context
    // (locked baseline if a project scope already exists, locked rows
    // for already-granted envs) and constrains env-upgrade levels
    // accordingly.
    const [scopes, setScopes] = useState<ScopeSelection>(
        freshScopeSelection(props.envOptions, "none"),
    );
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (props.open) {
            setScopes(freshScopeSelection(props.envOptions, "none"));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.open, props.envOptions]);

    return (
        <Modal
            title={props.keyName ? `Add scope to ${props.keyName}` : "Add scope"}
            open={props.open}
            onCancel={props.onClose}
            footer={
                <Space>
                    <Button onClick={props.onClose}>Cancel</Button>
                    <Button
                        type="primary"
                        loading={submitting}
                        disabled={scopeCount(scopes) === 0}
                        onClick={async () => {
                            setSubmitting(true);
                            try {
                                await props.onSubmit(scopes);
                            } finally {
                                setSubmitting(false);
                            }
                        }}
                    >
                        Add {scopeCount(scopes) || ""} {scopeCount(scopes) === 1 ? "scope" : "scopes"}
                    </Button>
                </Space>
            }
            width={560}
            destroyOnClose
        >
            <ScopePicker
                projectId={props.projectId}
                envOptions={props.envOptions}
                value={scopes}
                onChange={setScopes}
                existingScopes={props.existingScopes}
            />
        </Modal>
    );
}

// ────────────────────────────────────────────────────────────────────────────────
// Scope picker (reused in create + add-scope modals)
// ────────────────────────────────────────────────────────────────────────────────

function ScopePicker(props: {
    projectId: string;
    envOptions: EnvOption[];
    value: ScopeSelection;
    onChange: (next: ScopeSelection) => void;
    /**
     * Existing scopes already on the key (add-scope context). Used to:
     *   • lock the baseline radio at the existing project-scope level,
     *   • show already-granted envs as locked rows,
     *   • constrain new env-upgrade options to be strictly stronger than
     *     the effective baseline.
     * Empty for the create flow.
     */
    existingScopes?: ApiKeyScope[];
}) {
    const existing = props.existingScopes ?? [];
    const existingProjectScope = existing.find(
        s => s.entityType === "project" && s.entityId === props.projectId,
    );
    const existingEnvScopeByEnvId = new Map(
        existing
            .filter(s => s.entityType === "stage")
            .map(s => [s.entityId, s] as const),
    );
    const baselineLocked = !!existingProjectScope;

    // Currently-chosen baseline in the form (separate from any existing
    // project scope, which is locked-in and shown read-only).
    const formBaseline: Baseline = props.value.project.selected
        ? props.value.project.permission
        : "none";
    // Effective baseline for the "must be strictly stronger" constraint.
    // If the key already has a project scope, that level acts as the
    // floor; otherwise it's whatever the user currently has in the form.
    const effectiveBaseline: Baseline = baselineLocked
        ? existingProjectScope!.permission
        : formBaseline;
    const allowedEnvPerms = permissionsStrongerThan(effectiveBaseline);
    const envUpgradesAvailable = allowedEnvPerms.length > 0;

    function setBaseline(b: Baseline) {
        if (baselineLocked) return;
        const newProject =
            b === "none"
                ? { ...props.value.project, selected: false }
                : { selected: true, permission: b };
        // Bump any env-upgrade permissions that are no longer strictly
        // stronger than the new baseline. Avoids leaving stale state
        // that the submit-side validation would have to filter out.
        const allowed = permissionsStrongerThan(b);
        const newEnvs: ScopeSelection["envs"] = {};
        for (const [id, e] of Object.entries(props.value.envs)) {
            const perm = allowed.includes(e.permission)
                ? e.permission
                : (allowed[0] ?? e.permission);
            newEnvs[id] = { ...e, permission: perm };
        }
        props.onChange({ project: newProject, envs: newEnvs });
    }

    function setEnvSelected(envId: string, selected: boolean) {
        const prev = props.value.envs[envId] ?? {
            selected: false,
            permission: allowedEnvPerms[0] ?? Permission.Write,
        };
        const perm = allowedEnvPerms.includes(prev.permission)
            ? prev.permission
            : (allowedEnvPerms[0] ?? prev.permission);
        props.onChange({
            ...props.value,
            envs: { ...props.value.envs, [envId]: { selected, permission: perm } },
        });
    }

    function setEnvPermission(envId: string, perm: Permission) {
        const prev = props.value.envs[envId] ?? { selected: true, permission: perm };
        props.onChange({
            ...props.value,
            envs: { ...props.value.envs, [envId]: { ...prev, permission: perm } },
        });
    }

    // ── Baseline section ─────────────────────────────────────────────
    const baselineSection = (
        <div>
            <Space size={6} align="center" style={{ marginBottom: 4 }}>
                <FolderTree size={14} />
                <Typography.Text strong>Baseline access</Typography.Text>
            </Space>
            <div style={{ marginBottom: 8 }}>
                <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                    Applies across every entity in this project (envs, pipelines, apps).
                    Pick <Typography.Text code>read</Typography.Text> to enable discovery (list APIs).
                    Pick <Typography.Text code>None</Typography.Text> for strict per-env keys.
                </Typography.Text>
            </div>
            <Radio.Group
                value={baselineLocked ? existingProjectScope!.permission : formBaseline}
                onChange={e => setBaseline(e.target.value as Baseline)}
                disabled={baselineLocked}
                optionType="button"
                buttonStyle="solid"
            >
                <Radio.Button value="none">None</Radio.Button>
                {PERMISSION_OPTIONS.map(p => (
                    <Radio.Button key={p} value={p}>{p}</Radio.Button>
                ))}
            </Radio.Group>
            {baselineLocked && (
                <div style={{ marginTop: 6 }}>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        Already granted at <Typography.Text code>{existingProjectScope!.permission}</Typography.Text>.
                        Remove it from the key card to change.
                    </Typography.Text>
                </div>
            )}
        </div>
    );

    // ── Per-env upgrades section ────────────────────────────────────
    const envsSection = (
        <div>
            <Space size={6} align="center" style={{ marginBottom: 4 }}>
                <Box size={14} />
                <Typography.Text strong>Per-environment upgrades</Typography.Text>
                <Typography.Text type="secondary">({props.envOptions.length})</Typography.Text>
            </Space>
            <div style={{ marginBottom: 8 }}>
                <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                    {effectiveBaseline === "none"
                        ? "Stronger access on specific environments. Set baseline to None for strict per-env access."
                        : `Must be strictly stronger than the baseline (${effectiveBaseline}).`}
                </Typography.Text>
            </div>
            {props.envOptions.length === 0 ? (
                <Card size="small">
                    <Typography.Text type="secondary">No environments in this project.</Typography.Text>
                </Card>
            ) : !envUpgradesAvailable ? (
                <Card size="small">
                    <Typography.Text type="secondary">
                        Baseline is at the strongest level — no upgrades available.
                    </Typography.Text>
                </Card>
            ) : (
                <Card size="small" bodyStyle={{ padding: 0 }}>
                    {props.envOptions.map((env, idx) => {
                        const existingEnv = existingEnvScopeByEnvId.get(env.id);
                        const isExisting = !!existingEnv;
                        const state =
                            props.value.envs[env.id] ?? { selected: false, permission: allowedEnvPerms[0] };
                        return (
                            <div
                                key={env.id}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 12,
                                    padding: "8px 12px",
                                    borderTop: idx > 0 ? "1px solid #f0f0f0" : undefined,
                                    background: isExisting ? "#fafafa" : undefined,
                                }}
                            >
                                <Checkbox
                                    checked={isExisting || state.selected}
                                    disabled={isExisting}
                                    onChange={e => setEnvSelected(env.id, e.target.checked)}
                                >
                                    <Typography.Text>{env.name}</Typography.Text>
                                </Checkbox>
                                <div style={{ flex: 1 }} />
                                {isExisting ? (
                                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                        Already granted: <Typography.Text code>{existingEnv!.permission}</Typography.Text>
                                    </Typography.Text>
                                ) : (
                                    <Select
                                        value={state.permission}
                                        disabled={!state.selected}
                                        onChange={p => setEnvPermission(env.id, p)}
                                        size="small"
                                        style={{ width: 100 }}
                                        options={allowedEnvPerms.map(p => ({ label: p, value: p }))}
                                    />
                                )}
                            </div>
                        );
                    })}
                </Card>
            )}
        </div>
    );

    return (
        <Space direction="vertical" size={20} style={{ width: "100%" }}>
            {baselineSection}
            {envsSection}
        </Space>
    );
}

function PermissionSelect(props: {
    value: Permission;
    disabled?: boolean;
    onChange: (p: Permission) => void;
}) {
    return (
        <Select
            value={props.value}
            disabled={props.disabled}
            onChange={props.onChange}
            size="small"
            style={{ width: 100 }}
            options={PERMISSION_OPTIONS.map(p => ({ label: p, value: p }))}
        />
    );
}

export default ProjectSettingsApiKeys;
